import * as THREE from "three";
import { Vector3 } from "opengeometry";
import { Glyphs } from "@opengeometry/openglyph";

import { ElementType } from "../elements/base-type";
import { OPElement, IPlaceable } from "../elements/op-element";
import { IShape } from "../shapes/base-type";
import { Event } from "../../../../utils/event";
import { Y_UP, degToRad } from "../../../../utils/transform";
import type { Vec3 } from "../types";
import {
  DatumOptionsBase,
  DatumType,
  DATUM_COLORS,
} from "./base-type";
import { buildDashedSegments } from "./internals/geometry";
import { applyVisibility, setProfileView, setModelView } from "./internals/visibility";
import { disposeSubElements } from "./internals/lifecycle";

export interface LevelOptions extends DatumOptionsBase {
  datumType: DatumType.LEVEL;
  /** World Y elevation. */
  elevation: number;
  /** Start point on the X-Z plane: `[x, z]`. */
  origin: [number, number];
  /** Signed length of the line along local +X (before rotation). */
  length: number;
  /** Plan-view rotation around the origin, degrees. Default 0. */
  rotationDeg?: number;
  /** Show the label at the start endpoint. Default true. */
  showStartLabel?: boolean;
  /** Show the label at the end endpoint. Default true. */
  showEndLabel?: boolean;
  /** Font size for the OpenGlyph label, in world units. Default 2.5. */
  fontSize?: number;
  /** Override for label text. Falls back to `${labelName}  ${formatElevation(elevation)}`. */
  headLabel?: string;
}

const LABEL_OFFSET = 0.2;
const DASH_LENGTH = 0.2;
const DASH_GAP = 0.1;

/**
 * Horizontal reference plane anchored at a fixed elevation. Equivalent
 * to an `IfcBuildingStorey` datum in BIM terms. Rendered as a single
 * dashed horizontal line at the given elevation, with optional plain
 * text labels at one or both ends showing the level name + elevation.
 */
export class Level extends OPElement implements IShape, IPlaceable {
  ogid: string = crypto.randomUUID();
  ogType: string = ElementType.DATUM_LEVEL;

  subElements2D: Map<string, THREE.Object3D> = new Map();
  subElements3D: Map<string, THREE.Object3D> = new Map();

  selected = false;
  edit = false;
  locked = false;

  onDatumUpdated: Event<null> = new Event();

  private _profileView = true;
  private _modelView = true;

  propertySet: LevelOptions = {
    labelName: "Level",
    datumType: DatumType.LEVEL,
    color: DATUM_COLORS.LEVEL,
    lineWidth: 1,
    visibility: { plan: true, model: true },
    elevation: 0,
    origin: [-5, 0],
    length: 10,
    rotationDeg: 0,
    showStartLabel: true,
    showEndLabel: true,
    fontSize: 2.5,
    headLabel: undefined,
  };

  constructor(config?: Partial<LevelOptions>) {
    super();
    if (config) {
      this.propertySet = {
        ...this.propertySet,
        ...config,
        visibility: {
          ...this.propertySet.visibility,
          ...(config.visibility ?? {}),
        },
      };
    }
    this.propertySet.ogid = this.ogid;
    this.setOPGeometry();
    const resolved = applyVisibility(
      this.subElements2D,
      this.subElements3D,
      this.propertySet.visibility,
    );
    this._profileView = resolved.plan;
    this._modelView = resolved.model;
  }

  move(delta: Vec3): void {
    const [x, z] = this.propertySet.origin;
    this.propertySet.origin = [x + delta[0], z + delta[2]];
    this.propertySet.elevation += delta[1];
    this.setOPGeometry();
    this.onDatumUpdated.trigger(null);
  }

  rotate(angleDeg: number, axis: Vec3 = Y_UP, anchor?: Vec3): void {
    if (axis[0] !== 0 || axis[1] !== 1 || axis[2] !== 0) {
      console.warn("Level.rotate: only Y-axis rotation is supported; ignoring axis override.");
    }
    if (anchor) {
      const [ox, oz] = this.propertySet.origin;
      const dx = ox - anchor[0];
      const dz = oz - anchor[2];
      const rad = degToRad(angleDeg);
      const cos = Math.cos(rad);
      const sin = Math.sin(rad);
      this.propertySet.origin = [
        anchor[0] + dx * cos - dz * sin,
        anchor[2] + dx * sin + dz * cos,
      ];
    }
    this.propertySet.rotationDeg = (this.propertySet.rotationDeg ?? 0) + angleDeg;
    this.setOPGeometry();
    this.onDatumUpdated.trigger(null);
  }

  get labelName(): string {
    return this.propertySet.labelName;
  }
  set labelName(value: string) {
    this.propertySet.labelName = value;
    this.setOPGeometry();
  }

  get datumType(): DatumType {
    return this.propertySet.datumType;
  }

  get elevation(): number {
    return this.propertySet.elevation;
  }
  set elevation(value: number) {
    this.propertySet.elevation = value;
    this.setOPGeometry();
    this.onDatumUpdated.trigger(null);
  }

  get profileView(): boolean {
    return this._profileView;
  }
  set profileView(value: boolean) {
    this._profileView = value;
    setProfileView(this.subElements2D, value);
  }

  get modelView(): boolean {
    return this._modelView;
  }
  set modelView(value: boolean) {
    this._modelView = value;
    setModelView(this.subElements3D, value);
  }

  setOPConfig(config: Partial<LevelOptions>): void {
    disposeSubElements(this.subElements2D, this.subElements3D);
    this.propertySet = {
      ...this.propertySet,
      ...config,
      visibility: {
        ...this.propertySet.visibility,
        ...(config.visibility ?? {}),
      },
    };
    this.setOPGeometry();
    this.setOPMaterial();
  }

  getOPConfig(): LevelOptions {
    return this.propertySet;
  }

  setOPMaterial(): void {
    // Intentionally empty — geometry rebuilds handle every visual update.
  }

  setOPGeometry(): void {
    disposeSubElements(this.subElements2D, this.subElements3D);

    const {
      elevation,
      origin,
      length,
      rotationDeg,
      color,
      labelName,
      headLabel,
      showStartLabel,
      showEndLabel,
      fontSize,
    } = this.propertySet;

    const drawColor = color ?? DATUM_COLORS.LEVEL;
    const theta = degToRad(rotationDeg ?? 0);
    const cos = Math.cos(theta);
    const sin = Math.sin(theta);
    const dir: [number, number] = [cos, sin];

    const startVec = new Vector3(origin[0], elevation, origin[1]);
    const endVec = new Vector3(
      origin[0] + dir[0] * length,
      elevation,
      origin[1] + dir[1] * length,
    );

    const line = buildDashedSegments(startVec, endVec, drawColor, DASH_LENGTH, DASH_GAP);
    this.add(line);
    this.subElements3D.set("line", line);

    const text =
      headLabel && headLabel.length > 0
        ? headLabel
        : `${labelName}  ${formatElevation(elevation)}`;
    const glyphSize = fontSize ?? 2.5;

    // Text rotates around Y so it reads along the line direction and stands
    // upright in the world (vertical text height, plane perpendicular to the
    // line). This is the section/elevation view orientation — readable when
    // the camera looks along the perpendicular to the level line.
    const labelRotationY = -theta;

    if (showEndLabel !== false) {
      this.placeLabel(endVec, dir, drawColor, text, glyphSize, labelRotationY, "end");
    }
    if (showStartLabel !== false) {
      const negDir: [number, number] = [-dir[0], -dir[1]];
      this.placeLabel(startVec, negDir, drawColor, text, glyphSize, labelRotationY, "start");
    }

    this.onDatumUpdated.trigger(null);
  }

  private placeLabel(
    tip: Vector3,
    outward: [number, number],
    color: number,
    text: string,
    glyphSize: number,
    rotationY: number,
    keySuffix: "start" | "end",
  ): void {
    const label = Glyphs.addGlyph(text, glyphSize, color, true);
    // OpenGlyph defaults to rotation.x = -π/2 (flat on X-Z plane). Override
    // to stand the text upright and align its reading direction with the line.
    label.rotation.set(0, rotationY, 0);

    // OpenGlyph centers the text geometry at the GlyphNode origin, so the
    // *position* is the text center. Shift by half the text size so the inner
    // edge lands at LABEL_OFFSET past the line tip and the text bottom sits
    // on the line — both ends then look symmetric and anchored.
    label.textMesh.geometry.computeBoundingBox();
    const bbox = label.textMesh.geometry.boundingBox!;
    const halfWidth = (bbox.max.x - bbox.min.x) * 0.5;
    const halfHeight = (bbox.max.y - bbox.min.y) * 0.5;
    const totalOffset = LABEL_OFFSET + halfWidth;

    label.position.set(
      tip.x + outward[0] * totalOffset,
      tip.y + halfHeight,
      tip.z + outward[1] * totalOffset,
    );
    this.add(label);
    this.subElements3D.set(`label${capitalize(keySuffix)}`, label);
  }
}

function formatElevation(value: number): string {
  if (value === 0) return "±0.000";
  const fixed = Math.abs(value).toFixed(3);
  return value > 0 ? `+${fixed}` : `-${fixed}`;
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
